import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Printer } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { cleanMarkdownForDisplay } from "@/services/documentTransferService";

interface PrintButtonProps {
  content: string;
  messageId: string;
}

interface PdfSettings {
  company_name?: string;
  header_text?: string;
  footer_text?: string;
  header_image_url?: string;
  font_family?: string;
  font_size_body?: number;
  font_size_footnote?: number;
  margin_top?: number;
  margin_bottom?: number;
  margin_left?: number;
  margin_right?: number;
  line_spacing?: number;
  paragraph_indent?: number;
  page_numbering_enabled?: boolean;
  page_numbering_position?: string;
  lawyer_name?: string;
  oab_number?: string;
  oab_state?: string;
  include_lawyer_signature?: boolean;
}

export const PrintButton = ({ content, messageId }: PrintButtonProps) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const { user } = useAuth();

  const loadUserPdfSettings = async (): Promise<PdfSettings> => {
    if (!user) return {};
    
    try {
      const { data, error } = await supabase
        .from('pdf_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar configurações PDF:', error);
        return {};
      }

      return data || {};
    } catch (error) {
      console.error('Erro ao carregar configurações PDF:', error);
      return {};
    }
  };

  const createPrintContent = (content: string, settings: PdfSettings) => {
    // Configurações de formatação jurídica
    const fontFamily = settings.font_family || 'Times New Roman';
    const fontSize = settings.font_size_body || 12;
    const footnoteSize = settings.font_size_footnote || 10;
    const marginTop = settings.margin_top || 30;
    const marginBottom = settings.margin_bottom || 20;
    const marginLeft = settings.margin_left || 30;
    const marginRight = settings.margin_right || 20;
    const lineSpacing = settings.line_spacing || 1.5;
    const paragraphIndent = settings.paragraph_indent || 20;
    
    // CORREÇÃO: Limpar conteúdo para impressão - remover tags HTML quebradas
    let sanitizedContent = content;
    
    // Se o conteúdo tem tags HTML, extrair apenas o texto
    if (sanitizedContent.includes('<') && sanitizedContent.includes('>')) {
      // Criar elemento temporário para extrair texto limpo
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizedContent;
      sanitizedContent = tempDiv.innerText || tempDiv.textContent || '';
    }
    
    // Limpar caracteres especiais
    sanitizedContent = cleanMarkdownForDisplay(sanitizedContent);
    
    // CORREÇÃO: Usar apenas texto limpo, sem processamento markdown para evitar HTML no print
    let processedContent = sanitizedContent
      .replace(/\n\n/g, '</p><p style="margin: 10px 0; text-align: justify; text-indent: ' + paragraphIndent + 'mm; line-height: ' + lineSpacing + ';">')
      .replace(/\n/g, '<br>')
      .replace(/^(.*)/, '<p style="margin: 10px 0; text-align: justify; text-indent: ' + paragraphIndent + 'mm; line-height: ' + lineSpacing + ';">$1')
      .replace(/$/, '</p>');

    // Processar formatação jurídica específica
    processedContent = processedContent
      .replace(/Art\.\s*(\d+º?)/g, '<strong>Art. $1</strong>')
      .replace(/§\s*(\d+º?)/g, '<strong>§ $1</strong>')
      .replace(/\b([IVX]+)\s*-/g, '<strong>$1 -</strong>')
      .replace(/\b([a-z])\)\s*/g, '<strong>$1)</strong> ');

    const lawyerSignature = settings.include_lawyer_signature && (settings.lawyer_name || settings.oab_number || settings.oab_state) ? `
      <div class="lawyer-signature">
        <div class="signature-line"></div>
        ${settings.lawyer_name ? `<div class="lawyer-name">${settings.lawyer_name}</div>` : ''}
        ${(settings.oab_number || settings.oab_state) ? `<div class="oab-info">OAB/${settings.oab_state || 'XX'} ${settings.oab_number || 'XXXXXX'}</div>` : ''}
      </div>
    ` : '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Documento Jurídico</title>
        <style>
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            body {
              margin: ${marginTop}mm ${marginRight}mm ${marginBottom}mm ${marginLeft}mm;
              font-family: '${fontFamily}', serif;
              line-height: ${lineSpacing};
              color: #374151;
              font-size: ${fontSize}pt;
            }
            .print-header {
              border-bottom: 2px solid #e5e7eb;
              padding-bottom: 15px;
              margin-bottom: 25px;
              display: flex;
              align-items: center;
              gap: 15px;
            }
            .print-header img {
              max-height: 60px;
              width: auto;
            }
            .print-header h2 {
              margin: 0 0 5px 0;
              color: #1f2937;
              font-size: ${fontSize + 6}pt;
              font-weight: bold;
            }
            .print-header p {
              margin: 0;
              font-size: ${footnoteSize}pt;
              color: #6b7280;
              line-height: 1.4;
            }
            .print-content {
              text-align: justify;
              line-height: ${lineSpacing};
              word-wrap: break-word;
            }
            .print-content p {
              margin: 10px 0;
              text-align: justify;
              text-indent: ${paragraphIndent}mm;
              line-height: ${lineSpacing};
            }
            .print-content h2, .print-content h3 {
              text-align: left;
              text-indent: 0;
              page-break-after: avoid;
            }
            .lawyer-signature {
              margin-top: 40px;
              padding-top: 20px;
              text-align: center;
              page-break-inside: avoid;
            }
            .signature-line {
              border-top: 1px solid #000;
              width: 200px;
              margin: 0 auto 15px auto;
            }
            .lawyer-name {
              font-size: ${fontSize}pt;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .oab-info {
              font-size: ${footnoteSize}pt;
              color: #374151;
            }
            .print-footer {
              border-top: 1px solid #e5e7eb;
              padding-top: 15px;
              margin-top: 30px;
              text-align: center;
              font-size: ${footnoteSize}pt;
              color: #6b7280;
              line-height: 1.4;
              page-break-inside: avoid;
            }
            .page-number {
              ${settings.page_numbering_position === 'centro-inferior' 
                ? 'position: fixed; bottom: 15mm; left: 50%; transform: translateX(-50%);' 
                : 'position: fixed; top: 15mm; right: 15mm;'
              }
              font-size: ${footnoteSize}pt;
              color: #6b7280;
            }
            .no-print {
              display: none !important;
            }
          }
          @media screen {
            body {
              font-family: '${fontFamily}', serif;
              line-height: ${lineSpacing};
              color: #374151;
              padding: 25px;
              max-width: 800px;
              margin: 0 auto;
              font-size: ${fontSize}pt;
            }
          }
        </style>
      </head>
      <body>
        ${settings.page_numbering_enabled ? '<div class="page-number">1</div>' : ''}
        
        <div class="print-header">
          ${settings.header_image_url ? `
            <div style="flex-shrink: 0;">
              <img src="${settings.header_image_url}" alt="Logo" />
            </div>
          ` : ''}
          <div style="flex: 1;">
            ${settings.company_name ? `
              <h2>${settings.company_name}</h2>
            ` : ''}
            ${settings.header_text ? `
              <p>${settings.header_text}</p>
            ` : ''}
          </div>
        </div>
        
        <div class="print-content">
          ${processedContent}
        </div>
        
        ${lawyerSignature}
        
        ${settings.footer_text ? `
          <div class="print-footer">
            <p>${settings.footer_text}</p>
          </div>
        ` : ''}
      </body>
      </html>
    `;
  };

  const handlePrint = async () => {
    if (isPrinting) return;

    setIsPrinting(true);
    
    try {
      console.log('🖨️ Iniciando impressão com configurações jurídicas personalizadas');
      
      // Carregar configurações do usuário
      const settings = await loadUserPdfSettings();
      console.log('📋 Configurações carregadas para impressão:', settings);
      
      // Criar conteúdo formatado
      const printContent = createPrintContent(content, settings);
      
      // Criar nova janela para impressão
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        throw new Error('Não foi possível abrir a janela de impressão. Verifique se o bloqueador de pop-ups está desabilitado.');
      }
      
      // Escrever conteúdo na nova janela
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Aguardar carregamento e imprimir
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
      
      console.log('✅ Impressão jurídica iniciada com sucesso');
      
      toast({
        title: "Impressão iniciada!",
        description: "A janela de impressão foi aberta com formatação jurídica ABNT.",
      });
      
    } catch (error) {
      console.error('❌ Erro ao iniciar impressão:', error);
      toast({
        title: "Erro ao imprimir",
        description: "Não foi possível iniciar a impressão. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={handlePrint}
          disabled={isPrinting}
          className="h-5 w-5 md:h-6 md:w-6"
          aria-label="Imprimir"
        >
          <Printer className="h-3 w-3 md:h-4 md:w-4 text-secondary-foreground/70" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>Imprimir com formatação jurídica ABNT</p>
      </TooltipContent>
    </Tooltip>
  );
};
